/**
 * Unified Rate Limiter Service for Gemini API
 * 
 * CRITICAL: All API calls MUST use this single rate limiter to prevent
 * exceeding Gemini API limits.
 * 
 * Free Tier Rate Limits (https://ai.google.dev/gemini-api/docs/rate-limits):
 * - Gemini 2.0 Flash: 15 RPM, 1,000,000 TPM, 200 RPD
 * - Gemini 2.0 Flash-Lite: 30 RPM, 1,000,000 TPM, 200 RPD
 * 
 * This service consolidates rate limiting that was previously duplicated
 * across geminiService.ts and enhancedAIService.ts.
 */

// ==================== Configuration ====================

export interface RateLimitConfig {
    requestsPerMinute: number;
    requestsPerDay: number;
    tokensPerMinute: number;
    minRequestInterval: number;
    maxQueueSize: number;
    queueTimeout: number;
}

// Free Tier - Conservative limits to stay within API quotas
const DEFAULT_CONFIG: RateLimitConfig = {
    requestsPerMinute: 12,          // 15 RPM limit, use 12 for safety margin
    requestsPerDay: 180,            // 200 RPD limit, use 180 for safety margin
    tokensPerMinute: 900000,        // 1M TPM limit, use 900K for safety
    minRequestInterval: 4000,       // Min 4s between requests (60s/15 RPM)
    maxQueueSize: 30,               // Max pending requests
    queueTimeout: 45000,            // 45s max wait time in queue
};

// Paid Tier 1 - Higher limits for billing-enabled accounts
const PREMIUM_CONFIG: RateLimitConfig = {
    requestsPerMinute: 800,         // Tier 1: 1000 RPM for Gemini 2.0 Flash
    requestsPerDay: 8000,           // Tier 1: 10000 RPD
    tokensPerMinute: 3500000,       // Tier 1: 4M TPM
    minRequestInterval: 100,        // Much faster for paid tier
    maxQueueSize: 100,
    queueTimeout: 90000,
};

// ==================== Types ====================

interface QueuedRequest<T> {
    id: string;
    execute: () => Promise<T>;
    resolve: (value: T) => void;
    reject: (error: Error) => void;
    addedAt: number;
    priority: 'high' | 'normal' | 'low';
}

interface RateLimiterStats {
    requestsThisMinute: number;
    requestsToday: number;
    queueSize: number;
    lastRequestTime: number | null;
    isLimited: boolean;
    nextAvailableSlot: number;
    dailyLimitReached: boolean;
}

// ==================== Unified Rate Limiter ====================

class GeminiRateLimiter {
    private config: RateLimitConfig;
    private requestTimes: number[] = [];
    private dailyCount: number = 0;
    private lastDailyReset: number = Date.now();
    private queue: QueuedRequest<any>[] = [];
    private isProcessing: boolean = false;
    private lastRequestTime: number = 0;
    
    constructor(config: RateLimitConfig = DEFAULT_CONFIG) {
        this.config = config;
        
        // Load persisted daily count
        this.loadDailyCount();
        
        // Start queue processor
        this.processQueue();
    }
    
    // ==================== Public Methods ====================
    
    /**
     * Execute a request through the rate limiter
     * Returns a promise that resolves when the request is allowed and completed
     */
    async execute<T>(
        task: () => Promise<T>,
        options: {
            priority?: 'high' | 'normal' | 'low';
            skipQueue?: boolean;
        } = {}
    ): Promise<T> {
        const { priority = 'normal', skipQueue = false } = options;
        
        // Check daily limit
        this.cleanupDaily();
        if (this.dailyCount >= this.config.requestsPerDay) {
            throw new RateLimitError(
                'Daily API limit reached. Please try again tomorrow.',
                'daily_limit',
                this.getTimeUntilDailyReset()
            );
        }
        
        // If skip queue and can request immediately
        if (skipQueue && this.canRequestNow()) {
            return this.executeNow(task);
        }
        
        // Check queue size
        if (this.queue.length >= this.config.maxQueueSize) {
            throw new RateLimitError(
                'Too many pending requests. Please wait.',
                'queue_full',
                5000
            );
        }
        
        // Add to queue
        return new Promise<T>((resolve, reject) => {
            const request: QueuedRequest<T> = {
                id: `req_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
                execute: task,
                resolve,
                reject,
                addedAt: Date.now(),
                priority
            };
            
            // Insert based on priority
            if (priority === 'high') {
                const firstNormalIndex = this.queue.findIndex(r => r.priority !== 'high');
                if (firstNormalIndex === -1) {
                    this.queue.push(request);
                } else {
                    this.queue.splice(firstNormalIndex, 0, request);
                }
            } else if (priority === 'low') {
                this.queue.push(request);
            } else {
                const firstLowIndex = this.queue.findIndex(r => r.priority === 'low');
                if (firstLowIndex === -1) {
                    this.queue.push(request);
                } else {
                    this.queue.splice(firstLowIndex, 0, request);
                }
            }
            
            // Ensure queue is processing
            this.processQueue();
        });
    }
    
    /**
     * Check if a request can be made immediately
     */
    canRequestNow(): boolean {
        this.cleanup();
        this.cleanupDaily();
        
        // Check daily limit
        if (this.dailyCount >= this.config.requestsPerDay) {
            return false;
        }
        
        // Check per-minute limit
        const recentRequests = this.requestTimes.filter(
            t => Date.now() - t < 60000
        ).length;
        
        if (recentRequests >= this.config.requestsPerMinute) {
            return false;
        }
        
        // Check minimum interval
        if (Date.now() - this.lastRequestTime < this.config.minRequestInterval) {
            return false;
        }
        
        return true;
    }
    
    /**
     * Get wait time until next request slot available
     */
    getWaitTime(): number {
        this.cleanup();
        
        // Check minimum interval first
        const intervalWait = Math.max(
            0,
            this.config.minRequestInterval - (Date.now() - this.lastRequestTime)
        );
        
        if (intervalWait > 0) {
            return intervalWait;
        }
        
        // Check rate limit
        const recentRequests = this.requestTimes.filter(t => Date.now() - t < 60000);
        
        if (recentRequests.length >= this.config.requestsPerMinute) {
            const oldestRequest = Math.min(...recentRequests);
            return 60000 - (Date.now() - oldestRequest) + 100; // +100ms buffer
        }
        
        return 0;
    }
    
    /**
     * Get current statistics
     */
    getStats(): RateLimiterStats {
        this.cleanup();
        this.cleanupDaily();
        
        const waitTime = this.getWaitTime();
        
        return {
            requestsThisMinute: this.requestTimes.filter(t => Date.now() - t < 60000).length,
            requestsToday: this.dailyCount,
            queueSize: this.queue.length,
            lastRequestTime: this.lastRequestTime || null,
            isLimited: !this.canRequestNow(),
            nextAvailableSlot: Date.now() + waitTime,
            dailyLimitReached: this.dailyCount >= this.config.requestsPerDay
        };
    }
    
    /**
     * Update configuration (e.g., for premium users)
     */
    setConfig(config: Partial<RateLimitConfig>): void {
        this.config = { ...this.config, ...config };
    }
    
    /**
     * Use premium configuration
     */
    enablePremiumLimits(): void {
        this.config = PREMIUM_CONFIG;
    }
    
    /**
     * Use standard configuration
     */
    enableStandardLimits(): void {
        this.config = DEFAULT_CONFIG;
    }
    
    /**
     * Clear the queue (e.g., on user logout)
     */
    clearQueue(): void {
        this.queue.forEach(request => {
            request.reject(new Error('Queue cleared'));
        });
        this.queue = [];
    }
    
    // ==================== Private Methods ====================
    
    private async executeNow<T>(task: () => Promise<T>): Promise<T> {
        this.recordRequest();
        return task();
    }
    
    private recordRequest(): void {
        const now = Date.now();
        this.requestTimes.push(now);
        this.lastRequestTime = now;
        this.dailyCount++;
        this.persistDailyCount();
    }
    
    private async processQueue(): Promise<void> {
        if (this.isProcessing || this.queue.length === 0) {
            return;
        }
        
        this.isProcessing = true;
        
        while (this.queue.length > 0) {
            const request = this.queue[0];
            
            // Check for timeout
            if (Date.now() - request.addedAt > this.config.queueTimeout) {
                this.queue.shift();
                request.reject(new RateLimitError(
                    'Request timed out in queue',
                    'timeout',
                    0
                ));
                continue;
            }
            
            // Wait for rate limit
            const waitTime = this.getWaitTime();
            if (waitTime > 0) {
                await new Promise(resolve => setTimeout(resolve, waitTime));
            }
            
            // Check daily limit again
            this.cleanupDaily();
            if (this.dailyCount >= this.config.requestsPerDay) {
                // Reject all remaining requests
                this.queue.forEach(req => {
                    req.reject(new RateLimitError(
                        'Daily API limit reached',
                        'daily_limit',
                        this.getTimeUntilDailyReset()
                    ));
                });
                this.queue = [];
                break;
            }
            
            // Execute the request
            this.queue.shift();
            this.recordRequest();
            
            try {
                const result = await request.execute();
                request.resolve(result);
            } catch (error) {
                request.reject(error as Error);
            }
        }
        
        this.isProcessing = false;
    }
    
    private cleanup(): void {
        const now = Date.now();
        // Keep requests from last 2 minutes for rate calculation
        this.requestTimes = this.requestTimes.filter(t => now - t < 120000);
    }
    
    private cleanupDaily(): void {
        const now = Date.now();
        const oneDayMs = 24 * 60 * 60 * 1000;
        
        if (now - this.lastDailyReset > oneDayMs) {
            this.dailyCount = 0;
            this.lastDailyReset = now;
            this.persistDailyCount();
        }
    }
    
    private getTimeUntilDailyReset(): number {
        const oneDayMs = 24 * 60 * 60 * 1000;
        return Math.max(0, oneDayMs - (Date.now() - this.lastDailyReset));
    }
    
    private persistDailyCount(): void {
        try {
            localStorage.setItem('gemini_daily_count', JSON.stringify({
                count: this.dailyCount,
                resetTime: this.lastDailyReset
            }));
        } catch (e) {
            // localStorage might be full or unavailable
            console.warn('[RateLimiter] Failed to persist daily count:', e);
        }
    }
    
    private loadDailyCount(): void {
        try {
            const stored = localStorage.getItem('gemini_daily_count');
            if (stored) {
                const { count, resetTime } = JSON.parse(stored);
                const oneDayMs = 24 * 60 * 60 * 1000;
                
                if (Date.now() - resetTime < oneDayMs) {
                    this.dailyCount = count;
                    this.lastDailyReset = resetTime;
                }
            }
        } catch (e) {
            // Ignore errors, start fresh
        }
    }
}

// ==================== Custom Error ====================

export class RateLimitError extends Error {
    public readonly type: 'rate_limit' | 'daily_limit' | 'queue_full' | 'timeout';
    public readonly retryAfter: number; // ms
    
    constructor(
        message: string,
        type: 'rate_limit' | 'daily_limit' | 'queue_full' | 'timeout',
        retryAfter: number
    ) {
        super(message);
        this.name = 'RateLimitError';
        this.type = type;
        this.retryAfter = retryAfter;
    }
}

// ==================== Singleton Export ====================

// Single shared instance - CRITICAL: Do not create multiple instances!
export const geminiRateLimiter = new GeminiRateLimiter();

// Export class for testing
export { GeminiRateLimiter };

// Export configs for reference
export { DEFAULT_CONFIG, PREMIUM_CONFIG };

export default geminiRateLimiter;

